import { Injectable, inject } from '@angular/core';
import { OrdonnerEnfantRequest, OperationOrdonnancementEnfant } from '@app/features/editeur/models/ordonner-enfant-request.model';
import { ResponseToObjetMaquetteVersion } from '@app/shared/decorators/response-to-objet-maquette';
import { StructureService } from '@app/shared/structure/structure.service';
import {
    CreerObjetMaquetteRequest,
    DescripteursContexteRequest,
    EnfantsOrdonnes,
    DescripteursEnqueteRequest,
    DescripteursObjetMaquetteRequest,
    DescripteursSyllabus,
    Enfant,
    FormatsEnseignementRequest,
    ObjetsMaquetteService as ObjetMaquetteApi,
    ObjetMaquetteHistory,
    ObjetMaquetteSummary
} from '@pcscol/odf-api-v1-angular';
import { Observable, concat } from 'rxjs';
import { ObjetMaquette } from '../models/objet-maquette.model';

@Injectable({
    providedIn: 'root'
})
export class ObjetMaquetteService {
    private _objetMaquetteApi = inject(ObjetMaquetteApi);
    private structureService = inject(StructureService);

    @ResponseToObjetMaquetteVersion()
    lireObjetMaquette(id: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.lireObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    lireHistorique(id: string): Observable<ObjetMaquetteHistory> {
        return this._objetMaquetteApi.lireHistorique(this.structureService.current$.getValue(), id);
    }

    listerContextes(id: string) {
        return this._objetMaquetteApi.listerContextes(this.structureService.current$.getValue(), id);
    }

    @ResponseToObjetMaquetteVersion()
    ajouterEnfant(id: string, enfant: Enfant): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.ajouterEnfant(
            this.structureService.current$.getValue(),
            id,
            enfant,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    retirerEnfant(id: string, idEnfant: string, ignoreConflict: boolean): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.retirerEnfant(
            this.structureService.current$.getValue(),
            id,
            idEnfant,
            ignoreConflict,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    creerObjetMaquette(creerObjetMaquetteRequest: CreerObjetMaquetteRequest): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.creerObjetMaquette(
            this.structureService.current$.getValue(),
            creerObjetMaquetteRequest,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    declarerPointInscriptionAdministrative(id: string, contexteId: string, version: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.declarerPointInscriptionAdministrative(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            version,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    retirerPointInscriptionAdministrative(
        id: string,
        contexteId: string,
        version: string,
        ignoreConflict: boolean
    ): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.retirerPointInscriptionAdministrative(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            version,
            ignoreConflict,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    inactiverPointInscriptionAdministrative(id: string, contexteId: string, version: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.desactiverPointInscriptionAdministrative(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            version,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    reactiverPointInscriptionAdministrative(id: string, contexteId: string, version: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.reactiverPointInscriptionAdministrative(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            version,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    validerPartieBasse(id: string, contexteId: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.validerPartieBasse(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    validerPartieHaute(id: string, contexteId: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.validerPartieHaute(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }
    @ResponseToObjetMaquetteVersion()
    modifierFormatsEnseignementObjetMaquette(
        id: string,
        version: string,
        formatsEnseignementRequest: FormatsEnseignementRequest
    ): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierFormatsEnseignementObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            version,
            formatsEnseignementRequest,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    modifierDescripteursObjetMaquette(
        id: string,
        version: string,
        descripteursObjetMaquetteRequest: DescripteursObjetMaquetteRequest
    ): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierDescripteursObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            version,
            descripteursObjetMaquetteRequest,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    modifierDescripteursContexte(
        id: string,
        contexteId: string,
        version: string,
        descripteursContexteRequest: DescripteursContexteRequest
    ): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierDescripteursContexte(
            this.structureService.current$.getValue(),
            id,
            contexteId,
            version,
            descripteursContexteRequest,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    modifierDescripteursEnqueteObjetMaquette(id: string, version: string, req: DescripteursEnqueteRequest): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierDescripteursEnqueteObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            version,
            req,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    modifierDescripteursSyllabusObjetMaquette(
        id: string,
        version: string,
        descripteursSyllabus: DescripteursSyllabus
    ): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierDescripteursSyllabusObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            version,
            descripteursSyllabus,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    modifierLienEnfant(id: string, idEnfant: string, temoinObligatoire: boolean): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierLienEnfant(
            this.structureService.current$.getValue(),
            id,
            idEnfant,
            { temoinObligatoire },
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    ordonnerEnfant(id: string, idEnfant: string, ordonnerEnfantRequest: OrdonnerEnfantRequest): Observable<EnfantsOrdonnes> {
        switch (ordonnerEnfantRequest.operation) {
            case OperationOrdonnancementEnfant.Debut:
                return this._objetMaquetteApi.ordonnerEnfantAuDebut(id, idEnfant);
            case OperationOrdonnancementEnfant.Fin:
                return this._objetMaquetteApi.ordonnerEnfantEnFin(id, idEnfant);
            case OperationOrdonnancementEnfant.Entre:
                return this._objetMaquetteApi.ordonnerEnfantEntreDeuxEnfants(id, idEnfant, ordonnerEnfantRequest.intervalle);
            default:
                throw new Error(`Opération d'ordonnancement inconnue`);
        }
    }

    @ResponseToObjetMaquetteVersion()
    modifierCodeObjetMaquette(id: string, version: string, code: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.modifierCodeObjetMaquette(
            this.structureService.current$.getValue(),
            id,
            version,
            { code },
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    rendreMutualise(id: string, version: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.rendreMutualise(
            this.structureService.current$.getValue(),
            id,
            version,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }

    @ResponseToObjetMaquetteVersion()
    supprimerMutualise(id: string, version: string): Observable<ObjetMaquette> {
        return (this._objetMaquetteApi.supprimerMutualise(
            this.structureService.current$.getValue(),
            id,
            version,
            'response'
        ) as unknown) as Observable<ObjetMaquette>;
    }
    supprimerObjetMaquette(id: string, version: string, ignoreConflict: boolean) {
        return this._objetMaquetteApi.supprimerObjetMaquette(this.structureService.current$.getValue(), id, version, ignoreConflict);
    }

    lireObjetMaquettesIds(ids: string[]): Observable<ObjetMaquetteSummary[]> {
        return this._objetMaquetteApi.listerObjetsMaquettesParId(this.structureService.current$.getValue(), ids);
    }

    associerEtDissocierPerimetresFormation(idObjetMaquette: string, perimetresAAjouter: string[], perimetresARetirer: string[]) {
        // requêtes de manière séquentielle sinon le back pète
        return concat(
            ...perimetresARetirer.map((perimetre: string) =>
                this._objetMaquetteApi.dissocierPerimetreObjetMaquette(
                    this.structureService.current$.getValue(),
                    idObjetMaquette,
                    perimetre
                )
            ),
            ...perimetresAAjouter.map((perimetre: string) =>
                this._objetMaquetteApi.associerPerimetreObjetMaquette(this.structureService.current$.getValue(), idObjetMaquette, perimetre)
            )
        );
    }

    lireObjetMaquettePerimetres(idObjetMaquette: string) {
        return this._objetMaquetteApi.lireObjetMaquettePerimetres(this.structureService.current$.getValue(), idObjetMaquette);
    }
}
